import { useMemo, useState } from 'react';
import { DynamicForm } from '@shadcn/components/dynamic-form';
import { useGetAuthConfig, useUpdateAuthConfig } from '@shadcn/hooks/use-auth';
import {
  authEmailProviderSchema,
  authFieldLabels,
  authGeneralSettingsSchema,
  type AuthGeneralSettingsSchema,
} from '@shadcn/lib/schemas/auth';
import { z } from 'zod';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Skeleton } from '~/components/ui/Skeleton';
import { IconButton } from '~/components/ui/IconButton';

function ProviderSettingsView({
  schema,
  title,
  initialValues: allInitialValues,
  onBack,
}: {
  schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>;
  title: string;
  initialValues: any;
  onBack: () => void;
}) {
  const { mutate: updateAuthConfig, isPending: isUpdatingConfig } = useUpdateAuthConfig();

  const actualSchema = 'shape' in schema ? schema : (schema._def.schema as z.ZodObject<any>);

  const handleUpdateAuthConfig = (formData: z.infer<typeof actualSchema>) => {
    const payload = Object.fromEntries(Object.entries(formData).filter(([_, value]) => value !== undefined));

    if (Object.keys(payload).length === 0) {
      alert('No changes to submit. Please modify a field to update.');
      return;
    }

    updateAuthConfig({
      payload,
    });
  };

  const formInitialValues = useMemo(() => {
    if (!allInitialValues) {
      return undefined;
    }

    const schemaKeys = Object.keys(actualSchema.shape);

    const result = schemaKeys.reduce(
      (acc, key) => {
        if (Object.prototype.hasOwnProperty.call(allInitialValues, key)) {
          acc[key] = allInitialValues[key];
        }

        return acc;
      },
      {} as Record<string, any>,
    );

    return result;
  }, [allInitialValues, actualSchema]);

  return (
    <div className="pt-12">
      <div className="flex items-center gap-2 text-bolt-ds-textPrimary mb-2">
        <IconButton variant="link" icon="i-heroicons:arrow-left size-4 mb-0.5" onClick={onBack} size="sm" />
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <DynamicForm
        schema={actualSchema}
        onSubmit={handleUpdateAuthConfig}
        isLoading={isUpdatingConfig}
        invertedFields={['mailer_autoconfirm']} // if mailer_autoconfirm is false, confirm email switch should be enabled
        initialValues={formInitialValues}
        labels={authFieldLabels}
        saveAutomatically
      />
    </div>
  );
}

export function AuthManager() {
  const { data: authConfigData, isLoading: isLoadingConfig, error: errorLoadingConfig } = useGetAuthConfig();

  const { mutate: updateAuthConfig, isPending: isUpdatingConfig } = useUpdateAuthConfig();

  const [selectedProvider, setSelectedProvider] = useState<{
    name: string;
    schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>;
  } | null>(null);

  const handleUpdateGeneralSettings = (formData: AuthGeneralSettingsSchema) => {
    const payload = Object.fromEntries(Object.entries(formData).filter(([_, value]) => value !== undefined));

    if (Object.keys(payload).length === 0) {
      alert('No changes to submit. Please modify a field to update.');
      return;
    }

    updateAuthConfig({
      payload,
    });
  };

  const providers: {
    name: string;
    icon: React.ReactNode;
    description: string;
    schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>;
  }[] = [
    {
      icon: <div className="i-heroicons:envelope size-5 text-bolt-ds-textPrimary" />,
      name: 'Email',
      description: 'Sign in with email and password',
      schema: authEmailProviderSchema,
    },
  ];

  const handleProviderClick = (provider: {
    name: string;
    schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>;
  }) => {
    setSelectedProvider(provider);
  };

  const formInitialValues = useMemo(() => {
    if (!authConfigData) {
      return undefined;
    }

    const schemaKeys = Object.keys(authGeneralSettingsSchema.shape);

    return schemaKeys.reduce(
      (acc, key) => {
        if (Object.prototype.hasOwnProperty.call(authConfigData, key)) {
          acc[key] = authConfigData[key as keyof typeof authConfigData];
        }

        return acc;
      },
      {} as Record<string, any>,
    );
  }, [authConfigData]);

  if (errorLoadingConfig) {
    return (
      <div className="pt-12">
        <Alert type="danger" title="Error loading auth settings">
          There was a problem loading your authentication configuration.
        </Alert>
      </div>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className="pt-12 space-y-2">
        <Skeleton className="h-12 w-full mb-8" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (selectedProvider) {
    return (
      <ProviderSettingsView
        schema={selectedProvider.schema}
        title={`${selectedProvider.name} Provider Settings`}
        initialValues={authConfigData}
        onBack={() => {
          setSelectedProvider(null);
        }}
      />
    );
  }

  return (
    <div className="pt-12">
      <div className="mb-6">
        <h2 className="text-base lg:text-xl font-semibold">Authentication</h2>
        <h2 className="font-semibold text-sm text-bolt-ds-textTertiary mt-6">General</h2>
        <DynamicForm
          schema={authGeneralSettingsSchema}
          invertedFields={['disable_signup']} // if disable_signup is false, new users should be allowed to sign-up
          onSubmit={handleUpdateGeneralSettings}
          isLoading={isUpdatingConfig}
          initialValues={formInitialValues}
          labels={authFieldLabels}
          saveAutomatically
        />
      </div>
      <div>
        <h2 className="font-semibold text-sm text-bolt-ds-textTertiary">Sign-in methods</h2>
        <p className="text-bolt-ds-textPrimary my-4 text-sm">Configure how users authenticate to your app.</p>
        <div className="border border-bolt-elements-borderColor rounded-md overflow-hidden bg-bolt-ds-surfaceOne">
          {providers.map((provider) => (
            <Button
              variant="ghost"
              size="lg"
              onClick={() => handleProviderClick(provider)}
              key={provider.name}
              truncate={false}
              className="bg-bolt-ds-surfaceAltLight rounded-none justify-start flex-row text-left h-auto px-4 py-3 w-full gap-3 border-b border-bolt-elements-borderColor last:border-b-0"
            >
              {provider.icon}
              <div className="flex-1">
                <h3 className="font-bold">{provider.name}</h3>
                <p className="text-bolt-ds-textSecondary text-sm">{provider.description}</p>
              </div>
              <div className="i-lucide:chevron-right size-5 text-bolt-ds-textTertiary" />
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
